/* Factumis App — Factures émises (liste + détail) + reçues */
import { useState, useEffect, Fragment } from 'react';
import { Icon } from '../lib/icons';
import { Button, ConfirmDialog, Field, Modal, StatusPill, EmptyState, Timeline, rowActivation, eur, LIFECYCLE } from '../components/ui';
import type { TimelineStep } from '../components/ui';
import { useGo, useCtx } from '../shell/nav';
import type { Invoice } from '../data/demo';
import { useInvoices, useReceived, useCatalog, amendInvoice, settleInvoice } from '../data/live';
import { LineBuilder, blankLine, linesToPayload, type Line } from '../components/LineBuilder';
import { apiGet, apiPost, apiPatch, apiDelete, errorMessage } from '../lib/api';
import type { ReactNode } from 'react';

/* Libellés courts (cf. MICRO_CATEGORIES dans Settings.tsx pour les libellés longs) :
   le sélecteur d'encaissement n'a pas la place pour les intitulés complets. */
const URSSAF_CATEGORIES_SHORT: { value: string; label: string }[] = [
  { value: 'vente_bic', label: 'Vente' },
  { value: 'prestation_bic', label: 'Prestations BIC' },
  { value: 'prestation_bnc', label: 'Prestations BNC' },
  { value: 'liberale_cipav', label: 'Cipav' },
];

const STATUS_FILTERS: [string, string][] = [
  ['all', 'Toutes'], ['new', 'Brouillons'], ['sent', 'Envoyées'],
  ['paid', 'Payées'], ['overdue', 'En retard'], ['rejected', 'Refusées'],
];

/* Export CSV client (UTF-8 + BOM pour Excel/accents), sans dépendance. */
function downloadCsv(filename: string, headers: string[], rows: (string | number)[][]) {
  const esc = (v: string | number) => {
    const s = String(v ?? '');
    return /[";\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const body = [headers, ...rows].map((r) => r.map(esc).join(';')).join('\r\n');
  const blob = new Blob(['﻿' + body], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

type ToolbarProps = {
  q: string;
  setQ: (v: string) => void;
  filter?: string;
  setFilter?: (v: string) => void;
  filters?: [string, string][];
  placeholder?: string;
  actions?: ReactNode;
};

function Toolbar(props: ToolbarProps) {
  return (
    <div className="tbl-toolbar">
      <div className="tbl-search">
        <Icon name="search" size={15} />
        <input placeholder={props.placeholder || 'Rechercher…'} value={props.q} onChange={(ev) => props.setQ(ev.target.value)} />
      </div>
      {(props.filters || []).map((f) => (
        <button key={f[0]} className={'chipfilter' + (props.filter === f[0] ? ' on' : '')} onClick={() => props.setFilter?.(f[0])}>
          {f[1]}
        </button>
      ))}
      <div className="grow" />
      {props.actions}
    </div>
  );
}

export function InvoicesList() {
  const go = useGo();
  const ctx = useCtx();
  const [q, setQ] = useState((ctx.q as string) || '');
  const [filter, setFilter] = useState('all');
  useEffect(() => { if (ctx.q != null) setQ(ctx.q as string); }, [ctx]);
  const { rows: invoices, source, reload } = useInvoices();
  const rows = invoices.filter((iv) =>
    (filter === 'all' || iv.status === filter) &&
    (iv.client.toLowerCase().includes(q.toLowerCase()) || iv.id.toLowerCase().includes(q.toLowerCase())));

  return (
    <div>
      <div className="shead">
        <div className="shead__l">
          <h1>Factures émises</h1>
          <p>Suivez le cycle de vie de chaque facture, de l’envoi au paiement.</p>
        </div>
        <div className="shead__r">
          <Button
            variant="ghost"
            icon="download"
            disabled={rows.length === 0}
            onClick={() =>
              downloadCsv(
                'factures-emises.csv',
                ['Numéro', 'Client', 'Date', 'Échéance', 'Montant', 'Statut'],
                rows.map((iv) => [iv.id, iv.client, iv.date, iv.due, iv.amount, LIFECYCLE[iv.status]?.label ?? iv.status]),
              )
            }
          >
            Exporter
          </Button>
          <Button variant="primary" icon="plus" onClick={() => go('create')}>Nouvelle facture</Button>
        </div>
      </div>

      <div className="tbl-wrap">
        <Toolbar q={q} setQ={setQ} filter={filter} setFilter={setFilter} filters={STATUS_FILTERS} placeholder="Rechercher une facture, un client…" />
        {source === 'error' ? (
          <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '40px 20px' }}>
            <span className="muted">Impossible de charger les données.</span>
            <Button variant="ghost" size="sm" onClick={reload}>Réessayer</Button>
          </div>
        ) : source === 'live' && rows.length === 0 ? (
          <EmptyState
            glyph="file-out"
            title="Aucune facture pour le moment"
            actions={<Button variant="primary" icon="plus" onClick={() => go('create')}>Créer ma première facture</Button>}
          >
            Dès que vous émettez une facture, elle apparaît ici avec son statut en temps réel.
          </EmptyState>
        ) : (
          <Fragment>
            <div className="hide-mob">
              <table className="tbl">
                <thead>
                  <tr>
                    <th><span className="srt">Client<Icon name="sort" size={12} /></span></th>
                    <th>Numéro</th>
                    <th><span className="srt">Date<Icon name="sort" size={12} /></span></th>
                    <th>Échéance</th>
                    <th className="right">Montant</th>
                    <th>Statut</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((iv) => (
                    <tr key={iv.id} {...rowActivation(() => go('invoice', iv))}>
                      <td>
                        <div className="who-cell">
                          <span className="avatar" style={{ width: 30, height: 30, fontSize: 11 }}>{iv.client.slice(0, 2).toUpperCase()}</span>
                          <b>{iv.client}</b>
                        </div>
                      </td>
                      <td className="num">{iv.id}</td>
                      <td className="muted">{iv.date}</td>
                      <td className="muted">{iv.due}</td>
                      <td className="right amt">{eur(iv.amount)}</td>
                      <td><StatusPill status={iv.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="hide-desk" style={{ padding: 14 }}>
              <div className="mlist">
                {rows.map((iv) => (
                  <div key={iv.id} className="mrow" {...rowActivation(() => go('invoice', iv))}>
                    <div className="mrow__top"><b>{iv.client}</b><StatusPill status={iv.status} /></div>
                    <div className="mrow__meta"><span className="num">{iv.id + ' · ' + iv.date}</span><span className="amt">{eur(iv.amount)}</span></div>
                  </div>
                ))}
              </div>
            </div>
            <div className="tbl-foot">
              <span className="muted mono" style={{ fontSize: 12 }}>{rows.length + ' factures'}</span>
            </div>
          </Fragment>
        )}
      </div>
    </div>
  );
}

function timelineFor(status: string): TimelineStep[] {
  const order = ['new', 'sent', 'received', 'approved', 'paid'];
  const labels: Record<string, [string, string]> = {
    new: ['Créée', 'Brouillon enregistré'], sent: ['Envoyée', 'Transmise via la plateforme agréée'],
    received: ['Reçue par le client', 'Distribuée à son espace'], approved: ['Acceptée', 'Validée par le client'], paid: ['Payée', 'Règlement reçu'],
  };
  const neg: Record<string, string> = { overdue: 'sent', rejected: 'sent', litige: 'received', error: 'new' };
  let reached = order.indexOf(status);
  if (reached < 0) reached = order.indexOf(neg[status] || 'sent');
  const steps: TimelineStep[] = order.map((k, i) => ({
    label: labels[k][0], when: i <= reached ? labels[k][1] : undefined,
    state: i < reached ? 'done' : i === reached ? 'current' : 'future',
  }));
  return steps;
}

/* Déclenche le téléchargement d'un artefact via une ancre : les cookies same-origin
   authentifient le GET, pas besoin de fetch + blob. */
// Télécharge un document en vérifiant la réponse : sans ça, une erreur serveur
// (ex. 502 quand la plateforme agréée refuse un brouillon sans PDF) était enregistrée
// comme un fichier « pdf.json » illisible. On récupère le blob seulement si 2xx.
// Renvoie null si tout s'est bien passé, sinon le message à afficher : l'écran
// appelant l'affiche dans une modale, cette fonction ne connaît pas l'UI.
async function downloadUrl(url: string, filename: string): Promise<string | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return 'Le PDF et le XML sont générés une fois la facture émise. Ce document n’est pas encore disponible.';
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objUrl);
    return null;
  } catch {
    return 'Le téléchargement n’a pas abouti. Réessayez dans un instant.';
  }
}

type InvoiceActionProps = {
  status: string;
  invoiceId?: string;
  onSend: () => void;
  sending: boolean;
  onRemind: () => void;
  reminding: boolean;
  onDelete?: () => void;
  deleting?: boolean;
  /** Remonte à l'écran le message d'un téléchargement qui n'a pas abouti. */
  onNotice: (message: string) => void;
};

function actionsFor(p: InvoiceActionProps): ReactNode[] {
  // PDF/XML : disponibles seulement pour une facture réelle ET émise. La plateforme
  // agréée ne génère pas de document pour un brouillon ('new') → on désactive avec un
  // tooltip plutôt que de laisser le téléchargement échouer.
  const isDraft = p.status === 'new';
  const download = (kind: 'pdf' | 'xml') => {
    void downloadUrl(`/api/b2b/invoices/${p.invoiceId}/${kind}`, `facture-${p.invoiceId}.${kind}`).then((msg) => {
      if (msg) p.onNotice(msg);
    });
  };
  const dl = p.invoiceId && !isDraft
    ? [
        <Button key="pdf" variant="ghost" icon="download" onClick={() => download('pdf')}>PDF</Button>,
        <Button key="xml" variant="ghost" icon="download" onClick={() => download('xml')}>XML</Button>,
      ]
    : [
        <Button key="pdf" variant="ghost" icon="download" disabled title={p.invoiceId ? "Disponible après l'émission de la facture" : 'Facture de démonstration'}>PDF</Button>,
        <Button key="xml" variant="ghost" icon="download" disabled title={p.invoiceId ? "Disponible après l'émission de la facture" : 'Facture de démonstration'}>XML</Button>,
      ];
  const relancer = (
    <Button
      key="r"
      variant={p.status === 'overdue' ? 'primary' : 'ghost'}
      icon="bell"
      disabled={!p.invoiceId || p.reminding}
      loading={p.reminding}
      title={p.invoiceId ? undefined : 'Facture de démonstration'}
      onClick={p.onRemind}
    >
      {p.reminding ? 'Envoi…' : 'Relancer'}
    </Button>
  );
  if (p.status === 'new')
    return [
      <Button key="s" variant="primary" icon="send" onClick={p.onSend} disabled={p.sending} loading={p.sending}>
        {p.sending ? 'Envoi…' : 'Envoyer'}
      </Button>,
      p.invoiceId && p.onDelete ? (
        <Button key="del" variant="ghost" icon="trash" disabled={p.deleting} loading={p.deleting} onClick={p.onDelete}>
          {p.deleting ? 'Suppression…' : 'Supprimer'}
        </Button>
      ) : null,
      ...dl,
    ];
  if (p.status === 'overdue') return [relancer, ...dl];
  if (p.status === 'rejected') return [<Button key="d" variant="primary" icon="copy" disabled title="Bientôt disponible">Corriger &amp; renvoyer</Button>, ...dl];
  if (p.status === 'paid') return [...dl, <Button key="c" variant="ghost" icon="copy" disabled title="Bientôt disponible">Dupliquer</Button>];
  return [relancer, ...dl];
}

// Type de document lié créable depuis une facture.
type DocMode = 'credit_note' | 'corrective' | 'settle';

const DOC_META: Record<DocMode, { title: string; intro: string; reason: boolean; cta: string }> = {
  credit_note: { title: 'Créer un avoir', reason: true, cta: 'Créer l’avoir',
    intro: 'L’avoir réduit le montant de la facture source. Saisissez les lignes à annuler (montants positifs ; elles seront déduites automatiquement).' },
  corrective: { title: 'Facture rectificative', reason: true, cta: 'Créer la rectificative',
    intro: 'La rectificative remplace la facture source. Saisissez les lignes de la facture corrigée complète.' },
  settle: { title: 'Facture de solde', reason: false, cta: 'Créer le solde',
    intro: 'Cette facture est traitée comme un acompte. Saisissez les lignes de la facture COMPLÈTE : l’acompte déjà facturé sera déduit automatiquement.' },
};

/** Modale de création d'un document lié (avoir / rectificative / solde d'acompte). */
function DocModal({ mode, sourceInvoiceId, onClose, onCreated }: {
  mode: DocMode | null;
  sourceInvoiceId: string;
  onClose: () => void;
  onCreated: (inv: Invoice) => void;
}) {
  const [lines, setLines] = useState<Line[]>([blankLine()]);
  const catalog = useCatalog();
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (mode) { setLines([blankLine()]); setReason(''); setError(null); }
  }, [mode]);

  if (!mode) return null;
  const meta = DOC_META[mode];
  const ht = lines.reduce((s, l) => s + l.q * l.pu, 0);
  const tva = lines.reduce((s, l) => s + l.q * l.pu * (l.tva / 100), 0);

  async function submit() {
    if (!mode) return; // re-narrow dans la closure (mode ne peut pas être null ici)
    const payloadLines = linesToPayload(lines);
    if (payloadLines.length === 0) { setError('Ajoutez au moins une ligne.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const inv = mode === 'settle'
        ? await settleInvoice(sourceInvoiceId, { lines: payloadLines })
        : await amendInvoice(sourceInvoiceId, { kind: mode, reason: reason.trim() || undefined, lines: payloadLines });
      onCreated(inv);
    } catch (err) {
      setError(errorMessage(err, 'Création impossible. Réessayez.'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open onClose={() => (submitting ? null : onClose())} title={meta.title} wide>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>{meta.intro}</p>

        <div style={{ marginTop: 6 }}>
          <div className="kpi__k" style={{ marginBottom: 8 }}>Lignes</div>
          <LineBuilder lines={lines} setLines={setLines} catalog={catalog.rows} />
        </div>

        <div className="totals" style={{ marginTop: 14 }}>
          <div className="row"><span className="muted">Total HT</span><span className="v">{eur(ht)}</span></div>
          <div className="row"><span className="muted">TVA</span><span className="v">{eur(tva)}</span></div>
          <div className="row grand"><span>Total TTC</span><span className="v">{eur(ht + tva)}</span></div>
        </div>

        {meta.reason ? (
          <Field label="Motif" opt="optionnel" help="Raison de l’avoir / de la rectification (tracée sur le document).">
            <input className="ctrl" placeholder="Geste commercial, erreur de quantité…" value={reason} onChange={(ev) => setReason(ev.target.value)} />
          </Field>
        ) : null}

        {error ? (
          <div className="callout-err" style={{ marginTop: 4, marginBottom: 4 }}>
            <Icon name="alert" size={18} />
            <div><div className="t">Action impossible</div><div className="d">{error}</div></div>
          </div>
        ) : null}
      </div>

      <div className="acts">
        <Button variant="ghost" onClick={onClose} disabled={submitting}>Annuler</Button>
        <Button variant="primary" icon="check" onClick={() => void submit()} disabled={submitting}>
          {submitting ? 'Création…' : meta.cta}
        </Button>
      </div>
    </Modal>
  );
}

// Date locale (pas UTC) : toISOString() bascule sur le fuseau UTC, donc entre
// minuit et ~2h du matin en France elle renvoie encore la veille. On assemble
// la date à partir des composants locaux pour que le défaut et le max de
// l'input collent au jour réel de l'utilisateur.
const todayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/** Encaissement d'une facture émise (spec 2026-07-17) : marquer avec une date
    (par défaut aujourd'hui, antidatable), puis correction de la date si besoin.
    La garde métier (facture émise, non déjà encaissée…) est côté serveur ;
    ici on masque juste le bloc pour les brouillons et les factures refusées. */
/** Lignes locales d'une facture émise (GET /b2b/invoices/:id). Quand le serveur
    n'a pas de lignes exploitables (facture reçue, importée, historique), on
    retombe sur le message existant : le PDF Factur-X fait foi. */
type DetailLine = { description: string; quantity: number; unitPriceCents: number; vatRate: number };
type DetailTotals = { htCents: number; tvaCents: number; ttcCents: number };

function InvoiceLinesBlock({ invoiceId }: { invoiceId?: string }): JSX.Element {
  const [loading, setLoading] = useState(true);
  const [lines, setLines] = useState<DetailLine[] | null>(null);
  const [totals, setTotals] = useState<DetailTotals | null>(null);
  useEffect(() => {
    if (!invoiceId) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    apiGet<{ lines: DetailLine[] | null; totals: DetailTotals | null }>(`/api/b2b/invoices/${invoiceId}`)
      .then((r) => { if (!alive) return; setLines(r.lines); setTotals(r.totals); setLoading(false); })
      .catch(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [invoiceId]);

  if (loading) return <p className="muted" style={{ fontSize: 13, margin: 0 }}>Chargement du détail…</p>;
  if (!lines) {
    return (
      <p className="muted" style={{ fontSize: 13, margin: 0 }}>
        {invoiceId
          ? 'Le détail ligne à ligne figure sur la facture Factur-X : téléchargez le PDF ou le XML ci-dessus.'
          : 'Détail complet indisponible pour cette facture.'}
      </p>
    );
  }
  // Les totaux sont AU-DESSUS du tableau et hors de son conteneur scrollable :
  // placés à droite sous les lignes, ils sortaient de l'écran en mobile, obligeant
  // à faire glisser la page pour lire le montant de sa propre facture (audit QW2).
  return (
    <Fragment>
      {totals ? (
        <div className="lines-totals">
          <div className="lines-totals__item">
            <span className="kpi__k">Total HT</span>
            <b className="amt">{eur(totals.htCents / 100)}</b>
          </div>
          <div className="lines-totals__item">
            <span className="kpi__k">TVA</span>
            <b className="amt">{eur(totals.tvaCents / 100)}</b>
          </div>
          <div className="lines-totals__item lines-totals__item--grand">
            <span className="kpi__k">Total TTC</span>
            <b className="amt">{eur(totals.ttcCents / 100)}</b>
          </div>
        </div>
      ) : null}
      <div className="tbl-wrap">
        <table className="tbl">
          <thead>
            <tr><th>Désignation</th><th className="right">Qté</th><th className="right">P.U. HT</th><th className="right">TVA</th><th className="right">Total HT</th></tr>
          </thead>
          <tbody>
            {lines.map((l, i) => (
              <tr key={i}>
                <td>{l.description}</td>
                <td className="right num">{l.quantity}</td>
                <td className="right num">{eur(l.unitPriceCents / 100)}</td>
                <td className="right num">{l.vatRate} %</td>
                <td className="right amt">{eur(Math.round(l.quantity * l.unitPriceCents) / 100)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Fragment>
  );
}

function CashBlock({ invoiceId, status, onCashed }: { invoiceId: string; status: string; onCashed?: () => void }): JSX.Element | null {
  type Payment = { paymentDate: string; urssafCategory?: string | null };
  const [payment, setPayment] = useState<Payment | null | 'loading'>('loading');
  const [date, setDate] = useState(todayIso);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [micro, setMicro] = useState<{ enabled: boolean; mainCategory: string } | null>(null);
  const [category, setCategory] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    void apiGet<{ payment: Payment | null }>(`/api/b2b/invoices/${invoiceId}/cash`)
      .then((r) => {
        if (!alive) return;
        setPayment(r.payment);
        if (r.payment?.urssafCategory) setCategory(r.payment.urssafCategory);
      })
      .catch(() => { if (alive) setPayment(null); });
    return () => { alive = false; };
  }, [invoiceId]);

  useEffect(() => {
    let alive = true;
    void apiGet<{ profile: { enabled: boolean; mainCategory: string } | null }>('/api/b2b/micro/profile')
      .then((r) => {
        if (!alive || !r.profile?.enabled) return;
        setMicro(r.profile);
        setCategory((c) => c ?? r.profile!.mainCategory);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [invoiceId]);

  if (status === 'new' || status === 'rejected' || payment === 'loading') return null;

  async function submit(method: 'post' | 'patch'): Promise<void> {
    setBusy(true);
    setError(null);
    const body = { paymentDate: date, ...(micro && category ? { urssafCategory: category } : {}) };
    try {
      const r = method === 'post'
        ? await apiPost<{ payment: Payment }>(`/api/b2b/invoices/${invoiceId}/cash`, body)
        : await apiPatch<{ payment: Payment }>(`/api/b2b/invoices/${invoiceId}/cash`, body);
      setPayment(r.payment);
      setEditing(false);
      if (method === 'post') onCashed?.();
    } catch (err) {
      setError(errorMessage(err, "Impossible d'enregistrer l'encaissement."));
    } finally {
      setBusy(false);
    }
  }

  const dateInput = (
    <input
      type="date"
      className="ctrl"
      aria-label="Date d'encaissement"
      value={date}
      max={todayIso()}
      onChange={(ev) => setDate(ev.target.value)}
      style={{ width: 170 }}
    />
  );

  const categorySelect = micro ? (
    <select
      className="ctrl"
      aria-label="Catégorie URSSAF"
      value={category ?? micro.mainCategory}
      onChange={(ev) => setCategory(ev.target.value)}
      style={{ width: 170 }}
    >
      {URSSAF_CATEGORIES_SHORT.map((c) => (
        <option key={c.value} value={c.value}>{c.label}</option>
      ))}
    </select>
  ) : null;

  if (payment && !editing) {
    const categoryLabel = payment.urssafCategory
      ? URSSAF_CATEGORIES_SHORT.find((c) => c.value === payment.urssafCategory)?.label
      : null;
    return (
      <div style={{ marginTop: 12, display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span>Encaissée le {new Date(`${payment.paymentDate}T00:00:00`).toLocaleDateString('fr-FR')}</span>
        {categoryLabel ? <span className="muted">· {categoryLabel}</span> : null}
        <Button variant="ghost" onClick={() => { setDate(payment.paymentDate); setEditing(true); }}>Corriger</Button>
      </div>
    );
  }
  return (
    <div style={{ marginTop: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        {dateInput}
        {categorySelect}
        <Button variant="primary" icon="check-c" disabled={busy} loading={busy} onClick={() => void submit(payment ? 'patch' : 'post')}>
          {payment ? 'Enregistrer la date' : 'Marquer encaissée'}
        </Button>
        {editing ? <Button variant="ghost" onClick={() => setEditing(false)} disabled={busy}>Annuler</Button> : null}
      </div>
      {error ? <p className="muted" style={{ color: 'var(--danger, #b42318)', marginTop: 6 }}>{error}</p> : null}
    </div>
  );
}

export function InvoiceDetail() {
  const go = useGo();
  const ctx = useCtx();
  const { rows } = useInvoices();
  // Données réelles uniquement (aucun jeu de démo) : on prend l'enregistrement
  // passé à la navigation, sinon on le retrouve dans la liste live (deep-link /
  // rafraîchissement). L'API n'expose pas le détail ligne à ligne → le PDF fait foi.
  const iv: Invoice | null =
    (ctx.client && ctx.amount != null ? (ctx as unknown as Invoice) : null) ??
    rows.find((x) => x.id === ctx.id) ?? null;
  const invoiceId = (ctx.invoiceId as string | undefined) ?? iv?.invoiceId;
  const [status, setStatus] = useState<string>(iv?.status ?? 'new');
  useEffect(() => { if (iv) setStatus(iv.status); }, [iv?.id]);
  const [sending, setSending] = useState(false);
  const [sendErr, setSendErr] = useState<string | null>(null);
  const [reminding, setReminding] = useState(false);
  const [remindMsg, setRemindMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [docMode, setDocMode] = useState<DocMode | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Accusé en modale (à la place des window.alert natifs) : un téléchargement
  // qui n'aboutit pas, rien à décider pour l'utilisateur.
  const [notice, setNotice] = useState<string | null>(null);

  async function doSend() {
    if (!invoiceId) {
      setSendErr('Facture de démonstration : envoi indisponible.');
      return;
    }
    setSending(true);
    setSendErr(null);
    try {
      const r = await apiPost<{ invoice: { status: string } }>(`/api/b2b/invoices/${invoiceId}/send`, {});
      setStatus(r.invoice.status);
    } catch (err) {
      setSendErr(errorMessage(err, 'Envoi impossible. Réessayez dans un instant.'));
    } finally {
      setSending(false);
    }
  }

  // Suppression d'un brouillon : irréversible côté Seqino (numéro et jeton non
  // consommés à ce stade). Le retour à la liste (go('invoices')) démonte cet
  // écran et remonte InvoicesList, qui refetch depuis zéro : pas d'invalidation
  // de cache à gérer ici. La confirmation est portée par le ConfirmDialog du
  // rendu, cette fonction n'est appelée qu'une fois l'utilisateur décidé ; le
  // dialogue reste OUVERT pendant l'appel (busy) pour donner un retour visible,
  // et n'est refermé qu'au finally.
  async function doDelete() {
    if (!invoiceId) return;
    setDeleting(true);
    try {
      await apiDelete(`/api/b2b/invoices/${invoiceId}`);
      go('invoices'); // retour liste, le brouillon n'existe plus
    } catch (err) {
      setSendErr(errorMessage(err, 'Suppression impossible. Réessayez dans un instant.'));
    } finally {
      setDeleting(false);
      setConfirmDelete(false);
    }
  }

  async function doRemind() {
    if (!invoiceId) return;
    setReminding(true);
    setRemindMsg(null);
    try {
      await apiPost(`/api/b2b/invoices/${invoiceId}/remind`, {});
      setRemindMsg({ ok: true, text: 'Relance envoyée au client par email.' });
    } catch (err) {
      setRemindMsg({ ok: false, text: errorMessage(err, 'Relance impossible. Réessayez dans un instant.') });
    } finally {
      setReminding(false);
    }
  }

  if (!iv) {
    return (
      <div>
        <div className="shead">
          <div className="shead__l">
            <button className="btn btn--ghost btn--sm" onClick={() => go('invoices')} style={{ marginBottom: 12 }}>
              <Icon name="arrow-l" size={15} />Retour aux factures
            </button>
            <h1>Facture introuvable</h1>
            <p>Ouvrez une facture depuis la liste pour afficher son détail.</p>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div>
      <div className="shead">
        <div className="shead__l">
          <button className="btn btn--ghost btn--sm" onClick={() => go('invoices')} style={{ marginBottom: 12 }}>
            <Icon name="arrow-l" size={15} />Retour aux factures
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <h1>{iv.id}</h1><StatusPill status={status} />
          </div>
          <p>{'Émise le ' + iv.date + ' · échéance le ' + iv.due}</p>
        </div>
        <div className="shead__r">
          {actionsFor({
            status,
            invoiceId,
            onSend: () => void doSend(),
            sending,
            onRemind: () => void doRemind(),
            reminding,
            onDelete: () => setConfirmDelete(true),
            deleting,
            onNotice: setNotice,
          })}
        </div>
      </div>

      <ConfirmDialog
        open={confirmDelete}
        title="Supprimer ce brouillon ?"
        confirmLabel="Supprimer le brouillon"
        tone="danger"
        busy={deleting}
        onClose={() => setConfirmDelete(false)}
        onConfirm={() => void doDelete()}
      >
        Ce brouillon sera définitivement supprimé. Aucun numéro de facture ni jeton n’a été consommé, rien ne sera transmis
        à l’administration.
      </ConfirmDialog>

      <ConfirmDialog open={notice !== null} title="Document indisponible" onClose={() => setNotice(null)}>
        {notice ?? ''}
      </ConfirmDialog>

      {invoiceId ? <CashBlock invoiceId={invoiceId} status={status} onCashed={() => setStatus('paid')} /> : null}

      {remindMsg ? (
        <div className={remindMsg.ok ? 'callout-info' : 'callout-err'} style={{ marginBottom: 18 }}>
          <Icon name={remindMsg.ok ? 'check-c' : 'alert'} size={18} />
          <div>
            <div className="t">{remindMsg.ok ? 'Relance envoyée' : 'Relance impossible'}</div>
            <div className="d">{remindMsg.text}</div>
          </div>
        </div>
      ) : null}

      {sendErr ? (
        <div className="callout-err" style={{ marginBottom: 18 }}>
          <Icon name="alert" size={18} />
          <div>
            <div className="t">Envoi impossible</div>
            <div className="d">{sendErr}</div>
          </div>
        </div>
      ) : null}

      {status === 'rejected' ? (
        <div className="callout-err" style={{ marginBottom: 18 }}>
          <Icon name="alert" size={18} />
          <div>
            <div className="t">Facture refusée par le client</div>
            <div className="d">Consultez le motif auprès du client, corrigez puis renvoyez.</div>
          </div>
        </div>
      ) : null}

      <div className="wiz wiz--preview">
        <div>
          <div className="card">
            <div className="card__hd"><h3>Détail de la facture</h3></div>
            <div className="card--pad">
              <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 18 }}>
                <div>
                  <div className="kpi__k">Facturé à</div>
                  <b style={{ display: 'block', marginTop: 6, cursor: 'pointer', color: 'var(--laiton-text)' }} onClick={() => go('clients')}>{iv.client}</b>
                </div>
                <div>
                  <div className="kpi__k">Montant TTC</div>
                  <b className="amt" style={{ display: 'block', marginTop: 6 }}>{eur(iv.amount)}</b>
                </div>
              </div>
              <InvoiceLinesBlock invoiceId={invoiceId} />
            </div>
          </div>

          {invoiceId ? (
            <div className="card section-gap">
              <div className="card__hd"><h3>Documents liés</h3></div>
              <div className="card--pad">
                <p className="muted" style={{ fontSize: 13, marginTop: 0 }}>
                  Émettez un avoir, une facture rectificative, ou la facture de solde si cette facture était un acompte. Chaque document est créé en brouillon, lié à cette facture.
                </p>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 12 }}>
                  <Button variant="ghost" icon="file-out" onClick={() => setDocMode('credit_note')}>Créer un avoir</Button>
                  <Button variant="ghost" icon="edit" onClick={() => setDocMode('corrective')}>Rectifier</Button>
                  <Button variant="ghost" icon="file-out" onClick={() => setDocMode('settle')}>Facture de solde</Button>
                </div>
              </div>
            </div>
          ) : null}

          {/* Pas de carte « Note interne » tant que la fonctionnalité n'est pas
              livrée : le textarea était rendu au complet mais disabled, avec
              « Bientôt disponible. » en dessous (constat d'audit QW4). */}
        </div>

        <div>
          <div className="card card--pad">
            <h3 style={{ fontSize: 15, fontWeight: 600, marginBottom: 18 }}>Cycle de vie</h3>
            <Timeline steps={timelineFor(status)} />
          </div>
        </div>
      </div>

      {invoiceId ? (
        <DocModal
          mode={docMode}
          sourceInvoiceId={invoiceId}
          onClose={() => setDocMode(null)}
          onCreated={(created) => {
            setDocMode(null);
            // Bascule vers le document créé (brouillon). On passe l'objet facture complet
            // (comme un clic de ligne : go('invoice', iv)) pour que le détail s'affiche
            // directement — le détail résout par numéro, pas par invoiceId.
            go('invoice', created);
          }}
        />
      ) : null}
    </div>
  );
}

export function ReceivedList() {
  const go = useGo();
  const [q, setQ] = useState('');
  const { rows: received, source, reload } = useReceived();
  const rows = received.filter((r) => r.supplier.toLowerCase().includes(q.toLowerCase()));
  return (
    <div>
      <div className="shead">
        <div className="shead__l">
          <h1>Factures reçues</h1>
          <p>Validez, refusez ou réglez les factures de vos fournisseurs. Récupération automatique via la plateforme.</p>
        </div>
        <div className="shead__r">
          <Button variant="primary" icon="upload" disabled title="Bientôt disponible">Importer une facture</Button>
        </div>
      </div>
      <div className="tbl-wrap">
        <Toolbar q={q} setQ={setQ} filters={[]} placeholder="Rechercher un fournisseur…" />
        {source === 'error' ? (
          <div className="card" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, padding: '40px 20px' }}>
            <span className="muted">Impossible de charger les données.</span>
            <Button variant="ghost" size="sm" onClick={reload}>Réessayer</Button>
          </div>
        ) : source === 'live' && rows.length === 0 ? (
          <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
            <span className="muted">Aucune facture reçue.</span>
          </div>
        ) : (
          <>
            <div className="hide-mob">
              <table className="tbl">
                <thead>
                  <tr><th>Fournisseur</th><th>Numéro</th><th>Reçue le</th><th className="right">Montant</th><th>Statut</th><th></th></tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.id} {...rowActivation(() => go('received-detail', r))}>
                      <td>
                        <div className="who-cell">
                          <span className="avatar" style={{ width: 30, height: 30, fontSize: 11 }}>{r.supplier.slice(0, 2).toUpperCase()}</span>
                          <b>{r.supplier}</b>
                        </div>
                      </td>
                      <td className="num">{r.id}</td>
                      <td className="muted">{r.date}</td>
                      <td className="right amt">{eur(r.amount)}</td>
                      <td><StatusPill status={r.status} /></td>
                      <td className="right">
                        {r.status === 'tovalidate' ? (
                          <div style={{ display: 'inline-flex', gap: 6 }}>
                            <Button variant="soft" size="sm" onClick={(ev) => { ev.stopPropagation(); go('received-detail', r); }}>Valider</Button>
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="hide-desk" style={{ padding: 14 }}>
              <div className="mlist">
                {rows.map((r) => (
                  <div key={r.id} className="mrow" {...rowActivation(() => go('received-detail', r))}>
                    <div className="mrow__top"><b>{r.supplier}</b><StatusPill status={r.status} /></div>
                    <div className="mrow__meta"><span className="num">{r.id + ' · ' + r.date}</span><span className="amt">{eur(r.amount)}</span></div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
